import React, { useState, useCallback, useEffect } from 'react'
import {
  Form,
  Input,
  SelectPicker,
  TagPicker,
  Toggle,
  Button,
  ButtonGroup,
  Message,
  Panel
} from 'rsuite'

import MultipleShopPicker from '@components/common/commonFormInput/MultipleShopPicker'
import type { 
  ReportSchedule, 
  ReportType, 
  CreateReportScheduleInput, 
  UpdateReportScheduleInput 
} from '@@partner:types:app'

import './ReportScheduleForm.scss'
import { commonFieldChange } from '@@partner:helpers/util'

interface ReportScheduleFormProps {
  reportTypes: ReportType[]
  initialData?: ReportSchedule | null
  onSubmit: (data: CreateReportScheduleInput | UpdateReportScheduleInput) => Promise<void>
  onCancel: () => void
  loading?: boolean
}

// Preset cron schedules for common use cases
const CRON_PRESETS = [
  { label: 'Ежедневно в 9:00', value: '0 9 * * *' },
  { label: 'По будням в 9:00', value: '0 9 * * 1-5' },
  { label: 'Еженедельно (понедельник, 9:00)', value: '0 9 * * 1' },
  { label: 'Ежемесячно (1 число, 9:00)', value: '0 9 1 * *' },
  { label: 'Каждый час', value: '0 * * * *' },
  { label: 'Каждые 6 часов', value: '0 */6 * * *' }
]

const ReportScheduleForm: React.FC<ReportScheduleFormProps> = ({
  reportTypes,
  initialData,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [ formData, setFormData ] = useState<any>({
    report_type_id: '',
    name: '',
    shop_ids: [],
    frequency: '',
    recipients: [],
    subject: '',
    options: '{}',
    is_enabled: true
  })

  const [ formErrors, setFormErrors ] = useState<Record<string, string>>({})

  // Initialize form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        report_type_id: initialData.report_type_id || '',
        name: initialData.name || '',
        shop_ids: initialData.shops?.map(shop => shop.id) || [],
        frequency: initialData.frequency || '',
        recipients: Array.isArray(initialData.recipients)
          ? initialData.recipients
          : (typeof initialData.recipients === 'string' && initialData.recipients
            ? JSON.parse(initialData.recipients)
            : []),
        subject: initialData.subject || '',
        options: typeof initialData.options === 'string'
          ? initialData.options
          : JSON.stringify(initialData.options || {}),
        is_enabled: initialData.is_enabled ?? true
      })
    } 
    else {
      setFormData({
        report_type_id: '',
        name: '',
        shop_ids: [],
        frequency: '',
        recipients: [],
        subject: '',
        options: '{}',
        is_enabled: true
      })
    }
  }, [ initialData ])

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {}

    if (!formData.report_type_id) {
      errors.report_type_id = 'Выберите тип отчета'
    }

    if (!formData.name?.trim()) {
      errors.name = 'Укажите название расписания'
    }

    if (!Array.isArray(formData.recipients) || formData.recipients.length === 0) {
      errors.recipients = 'Укажите хотя бы один email для отправки'
    }

    if (!formData.frequency?.trim()) {
      errors.frequency = 'Укажите расписание выполнения'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }, [ formData ])

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(formData)
    } 
    catch (error) {
      console.error('Form submission error:', error)
    }
  }, [ formData, validateForm, onSubmit ])

  const handleFieldChange = commonFieldChange(setFormData)

  const reportTypeOptions = reportTypes.map(type => ({
    label: type.name,
    value: type.id
  }))

  return (
    <div className="ReportScheduleForm">
      <Form fluid>
        <Panel header="Основные настройки" bordered>
          <Form.Group controlId="report_type_id">
            <Form.ControlLabel>Тип отчета *</Form.ControlLabel>
            <SelectPicker
              data={reportTypeOptions}
              value={formData.report_type_id}
              onChange={handleFieldChange('report_type_id')}
              placeholder="Выберите тип отчета"
              block
              disabled={loading}
            />
            {formErrors.report_type_id && (
              <Message type="error" showIcon={false}>
                {formErrors.report_type_id}
              </Message>
            )}
          </Form.Group>

          <Form.Group controlId="name">
            <Form.ControlLabel>Название расписания *</Form.ControlLabel>
            <Input
              value={formData.name}
              onChange={handleFieldChange('name')}
              placeholder="Например: Ежедневный отчет по продажам"
              disabled={loading}
            />
            {formErrors.name && (
              <Message type="error" showIcon={false}>
                {formErrors.name}
              </Message>
            )}
          </Form.Group>

          <MultipleShopPicker
            value={formData.shop_ids}
            onChange={(shopIds: any) => handleFieldChange('shop_ids')(shopIds)}
            label="Магазины"
            placeholder="Выберите магазины для отчета"
            helpText="Если не выбрать магазины, отчет будет формироваться по всем магазинам"
            disabled={loading}
            error={formErrors.shop_ids}
          />

          <Form.Group controlId="subject">
            <Form.ControlLabel>Тема письма</Form.ControlLabel>
            <Input
              value={formData.subject}
              onChange={handleFieldChange('subject')}
              placeholder="Тема для email-уведомлений"
              disabled={loading}
            />
          </Form.Group>

          <Form.Group controlId="is_enabled">
            <Form.ControlLabel>Статус</Form.ControlLabel>
            <Toggle
              checked={formData.is_enabled}
              onChange={handleFieldChange('is_enabled')}
              checkedChildren="Активно"
              unCheckedChildren="Неактивно"
              disabled={loading}
            />
          </Form.Group>
        </Panel>

        <Panel header="Настройки отправки" bordered className="mt-4">
          <Form.Group controlId="recipients">
            <Form.ControlLabel>Email для отправки *</Form.ControlLabel>
            <TagPicker
              data={formData.recipients.map((email: string) => ({ 
                label: email, 
                value: email 
              }))}
              value={formData.recipients}
              onChange={handleFieldChange('recipients')}
              creatable
              placeholder="Введите email адреса"
              disabled={loading}
              block
            />
            {formErrors.recipients && (
              <Message type="error" showIcon={false}>
                {formErrors.recipients}
              </Message>
            )}
          </Form.Group>

          <Form.Group controlId="frequency">
            <Form.ControlLabel>Расписание выполнения *</Form.ControlLabel>
            <SelectPicker
              data={CRON_PRESETS}
              value={formData.frequency}
              onChange={handleFieldChange('frequency')}
              placeholder="Выберите готовое расписание или введите свое"
              searchable={false}
              block
              disabled={loading}
            />
            <Input
              value={formData.frequency}
              onChange={handleFieldChange('frequency')}
              placeholder="Или введите cron выражение (например: 0 9 * * *)"
              className="mt-2"
              disabled={loading}
            />
            {formErrors.frequency && (
              <Message type="error" showIcon={false}>
                {formErrors.frequency}
              </Message>
            )}
            <Message type="info" showIcon={false} className="mt-2">
              Формат cron: минута час день_месяца месяц день_недели
            </Message>
          </Form.Group>
        </Panel>

        <div className="mt-4">
          <ButtonGroup>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              loading={loading}
              disabled={loading}
            >
              {initialData ? 'Сохранить изменения' : 'Создать расписание'}
            </Button>
            <Button
              appearance="subtle"
              onClick={onCancel}
              disabled={loading}
            >
              Отмена
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </div>
  )
}

export default ReportScheduleForm
